/**
 * Card Components
 * A set of card components for content containers.
 * @packageDocumentation
 */

import { forwardRef, type ReactNode, type HTMLAttributes } from 'react';

import { cn } from '@/lib/utils';

/** Card visual variant. */
type CardVariant = 'default' | 'elevated' | 'glass';

/** Props for the Card component. */
interface CardProps extends HTMLAttributes<HTMLDivElement> {
  /** Visual style variant. */
  variant?: CardVariant;
}

const cardVariantStyles: Record<CardVariant, string> = {
  default: '',
  glass: 'glass',
  elevated: 'card-elevated',
};

/**
 * Card container component with optional visual variants.
 */
const Card = forwardRef<HTMLDivElement, CardProps>(({ className, variant = 'default', ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'rounded-lg border border-border bg-card text-card-foreground transition-shadow',
      cardVariantStyles[variant],
      className,
    )}
    {...props}
  />
));
Card.displayName = 'Card';

/**
 * Card header component with optional action slot.
 */
const CardHeader = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement> & {
    action?: ReactNode;
  }
>(({ action, children, className, ...props }, ref) => (
  <div ref={ref} className={cn('flex items-center justify-between gap-4 px-6 py-4', className)} {...props}>
    <div className="flex flex-col space-y-1.5">{children}</div>
    {action && <div>{action}</div>}
  </div>
));
CardHeader.displayName = 'CardHeader';

/**
 * Card title component.
 */
const CardTitle = forwardRef<HTMLHeadingElement, HTMLAttributes<HTMLHeadingElement>>(({ className, ...props }, ref) => (
  <h3 ref={ref} className={cn('text-base font-semibold leading-none tracking-tight', className)} {...props} />
));
CardTitle.displayName = 'CardTitle';

/**
 * Card description component.
 */
const CardDescription = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('text-sm text-muted-foreground leading-relaxed', className)} {...props} />
));
CardDescription.displayName = 'CardDescription';

/**
 * Card content component.
 */
const CardContent = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('p-6', className)} {...props} />
));
CardContent.displayName = 'CardContent';

/**
 * Card footer component.
 */
const CardFooter = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(({ className, ...props }, ref) => (
  <div ref={ref} className={cn('flex items-center border-t border-border/60 px-6 py-4', className)} {...props} />
));
CardFooter.displayName = 'CardFooter';

/**
 * Card toolbar component for search, filter, and action strips.
 */
const CardToolbar = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLDivElement> & {
    /** Enable sticky positioning. */
    sticky?: boolean;
    /** Right-side action element (button, badge). */
    action?: ReactNode;
    /** Top offset for sticky positioning (px number or CSS string like '4rem'). */
    stickyTop?: number | string;
  }
>(({ style, action, sticky, children, className, stickyTop, ...props }, ref) => (
  <div
    ref={ref}
    className={cn(
      'flex items-center justify-between gap-3 border-b border-border/60 px-6 py-4',
      sticky && 'sticky z-20 bg-card transition-shadow duration-200',
      className,
    )}
    style={
      sticky && stickyTop !== undefined
        ? { ...style, top: typeof stickyTop === 'number' ? `${stickyTop}px` : stickyTop }
        : style
    }
    {...props}
  >
    <div className="flex flex-1 items-center gap-4">{children}</div>
    {action && <div className="shrink-0">{action}</div>}
  </div>
));
CardToolbar.displayName = 'CardToolbar';

export { Card, CardHeader, CardFooter, CardToolbar, CardTitle, CardDescription, CardContent };
